import React, {useEffect, useState} from 'react'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {useGoogleReCaptcha} from 'react-google-recaptcha-v3'
import RCInput from '../../../utils/RCInput'
import RCSelect from '../../../utils/RCSelect'
import RCMultiSelect from '../../../utils/RCMultiSelect'
import RCDatePickerInput from '../../../utils/RCDatePickerInput'
import RCSwitch from '../../../utils/RCSwitch'
import RCLocationSelect from '../../../utils/RCLocationSelect'
import IAddress from '../../../core/interfaces/IAddress'
import PhotoUploadField from './shared/PhotoUploadField'
import {RACARequest} from '../../../core/api/Request'
import talentFormSignUpRequest from '../../../core/api/requests/public-signup/talentFormSignUpRequest'
import uploadSignUpFileRequest from '../../../core/api/requests/public-signup/uploadSignUpFileRequest'
import {useDispatch} from 'react-redux'
import {useIntl} from 'react-intl'
import {useParams} from 'react-router-dom'

const validationSchema = Yup.object().shape({
  name: Yup.string().required('El nombre es obligatorio'),
  lastName: Yup.string().required('Los apellidos son obligatorios'),
  email: Yup.string().email('Email no válido').required('El email es obligatorio'),
  phone: Yup.string().required('El teléfono es obligatorio'),
  address: Yup.string().required('La dirección es obligatoria'),
  nationality: Yup.array().min(1, 'La nacionalidad es obligatoria').required('La nacionalidad es obligatoria'),
  origin: Yup.array().min(1, 'El origen es obligatorio').required('El origen es obligatorio'),
  ethnicity: Yup.array().min(1, 'La etnia es obligatoria').required('La etnia es obligatoria'),
  birthDate: Yup.string().required('La fecha de nacimiento es obligatoria'),
  gender: Yup.string().required('El género es obligatorio'),
  hairColor: Yup.string().required('El color de pelo es obligatorio'),
  eyesColor: Yup.string().required('El color de ojos es obligatorio'),
  hairType: Yup.string().required('El tipo de pelo es obligatorio'),
  bodyType: Yup.string().required('El tipo de cuerpo es obligatorio'),
  languages: Yup.array().min(1, 'Los idiomas son obligatorios').required('Los idiomas son obligatorios'),
  dialects: Yup.array().min(1, 'Los dialectos son obligatorios').required('Los dialectos son obligatorios'),
  height: Yup.number().required('La altura es obligatoria').positive('Debe ser un número positivo'),
  dressSize: Yup.string().required('La talla de camisa es obligatoria'),
  pantSize: Yup.string().required('La talla de pantalón es obligatoria'),
  shoesSize: Yup.string().required('La talla de zapatos es obligatoria'),
  driverLicence: Yup.array().min(1, 'El carné de conducir es obligatorio').required('El carné de conducir es obligatorio'),
  actorCategory: Yup.array().min(1, 'El tipo de actor es obligatorio').required('El tipo de actor es obligatorio'),
  instruments: Yup.array().min(1, 'Los instrumentos son obligatorios').required('Los instrumentos son obligatorios'),
  dancing: Yup.array().min(1, 'Los bailes son obligatorios').required('Los bailes son obligatorios'),
  sports: Yup.array().min(1, 'Los deportes son obligatorios').required('Los deportes son obligatorios'),
})

const initialValues = {
  name: '',
  lastName: '',
  email: '',
  phone: '',
  secondaryPhone: '',
  instagram: '',
  address: '',
  nationality: [] as string[],
  origin: [] as string[],
  ethnicity: [] as string[],
  birthDate: '',
  gender: '',
  professions: [] as string[],
  lgtbiq: false,
  actorCategory: [] as string[],
  biography: '',
  height: '',
  bust: '',
  waist: '',
  hip: '',
  dressSize: '',
  pantSize: '',
  shoesSize: '',
  hairColor: '',
  hairType: '',
  hairLength: '',
  eyesColor: '',
  bodyType: '',
  skinType: '',
  glasses: false,
  contactLenses: false,
  languages: [] as string[],
  dialects: [] as string[],
  driverLicence: [] as string[],
  sports: [] as string[],
  dancing: [] as string[],
  instruments: [] as string[],
  singing: false,
}

function TalentosSignupForm() {
  const dispatch = useDispatch()
  const intl = useIntl()
  const {agencyId} = useParams<{agencyId: string}>()
  const {executeRecaptcha} = useGoogleReCaptcha()

  const getValuesAsArray = (min: number, max: number): number[] => {
    const values: number[] = []
    for (let i = min; i <= max; i++) {
      values.push(i)
    }
    return values
  }

  const clothingSizeOptions = [
    ...[3, 6, 9, 12, 18, 24].map((m) => ({ key: `${m} meses`, value: `${m} meses` })),
    ...getValuesAsArray(3, 16).map((y) => ({ key: `${y} años`, value: `${y} años` })),
    ...getValuesAsArray(32, 60).map((s) => ({ key: s.toString(), value: s.toString() })),
  ]

  const [futils, setFutils] = useState<any>(null)
  const [submitted, setSubmitted] = useState(false)
  const [selectedAddress, setSelectedAddress] = useState<IAddress | null>(null)
  const [submitError, setSubmitError] = useState('')
  const [closeUpPhoto, setCloseUpPhoto] = useState<File | null>(null)
  const [fullBodyPhoto, setFullBodyPhoto] = useState<File | null>(null)
  const [presentationVideo, setPresentationVideo] = useState<File | null>(null)
  const [fileErrors, setFileErrors] = useState<string[]>([])

  useEffect(() => {
    const req = new RACARequest('utils', 'futilsPublic')
    req.makeRequest().then((res: any) => {
      if (res.data) setFutils(res.data)
    })
  }, [])

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      setSubmitError('')
      setFileErrors([])

      const missingFiles: string[] = []
      if (!closeUpPhoto) missingFiles.push('Foto primer plano es obligatoria')
      if (!fullBodyPhoto) missingFiles.push('Foto cuerpo entero es obligatoria')
      if (!presentationVideo) missingFiles.push('Vídeo presentación es obligatorio')
      if (missingFiles.length > 0) {
        setFileErrors(missingFiles)
        return
      }

      try {
        let recaptchaToken = ''
        if (executeRecaptcha) {
          recaptchaToken = await executeRecaptcha('talent_form_signup')
        }

        const payload: any = {
          formType: 'advertising',
          recaptchaToken,
          name: values.name,
          lastName: values.lastName,
          email: values.email,
          phone: values.phone,
          secondaryPhone: values.secondaryPhone,
          instagram: values.instagram,
          address: selectedAddress || {fullAddress: values.address},
          nationality: values.nationality,
          origin: values.origin,
          ethnicity: values.ethnicity,
          birthDate: values.birthDate ? new Date(values.birthDate).toISOString() : '',
          gender: values.gender,
          professions: values.professions,
          lgtbiq: values.lgtbiq,
          modelTypes: ['Advertising'],
          actorCategory: values.actorCategory,
          biography: values.biography,
          height: values.height ? Number(values.height) : undefined,
          bust: values.bust ? Number(values.bust) : undefined,
          waist: values.waist ? Number(values.waist) : undefined,
          hip: values.hip ? Number(values.hip) : undefined,
          dressSize: values.dressSize,
          pantSize: values.pantSize,
          shoesSize: values.shoesSize,
          hairColor: values.hairColor,
          hairType: values.hairType,
          hairLength: values.hairLength,
          eyesColor: values.eyesColor,
          bodyType: values.bodyType,
          skinType: values.skinType,
          glasses: values.glasses,
          contactLenses: values.contactLenses,
          languages: values.languages,
          dialects: values.dialects,
          driverLicence: values.driverLicence,
          sports: values.sports,
          dancing: values.dancing,
          instruments: values.instruments,
          singing: values.singing,
        }

        const response = await talentFormSignUpRequest(dispatch, payload, agencyId)

        if (response.data && response.data.id) {
          const userId = response.data.id

          if (closeUpPhoto) {
            await uploadSignUpFileRequest(dispatch, userId, closeUpPhoto, 'photo', 'closeUp')
          }
          if (fullBodyPhoto) {
            await uploadSignUpFileRequest(dispatch, userId, fullBodyPhoto, 'photo', 'fullBody')
          }
          if (presentationVideo) {
            await uploadSignUpFileRequest(dispatch, userId, presentationVideo, 'video.presentation', 'presentation')
          }

          setSubmitted(true)
        } else {
          setSubmitError('Error al enviar el formulario. Por favor, inténtalo de nuevo.')
        }
      } catch (error: any) {
        setSubmitError(error?.message || 'Error al enviar el formulario. Por favor, inténtalo de nuevo.')
      }
    },
  })

  if (submitted) {
    return (
      <div className='container-fluid px-4 px-md-10 py-10' style={{maxWidth: '1400px', margin: '0 auto'}}>
        <div className='card'>
          <div className='card-body text-center py-20'>
            <h2 className='mb-5'>¡Formulario enviado correctamente!</h2>
            <p className='text-muted fs-5'>
              Gracias por registrarte. Revisaremos tu información y nos pondremos en contacto contigo.
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (!futils) {
    return (
      <div className='d-flex justify-content-center align-items-center py-20'>
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Cargando...</span>
        </div>
      </div>
    )
  }

  return (
    <div className='container-fluid px-4 px-md-10 py-10' style={{maxWidth: '1400px', margin: '0 auto'}}>
      <form onSubmit={formik.handleSubmit} noValidate>
        {/* Header */}
        <div className='card mb-5'>
          <div className='card-header border-0'>
            <div className='card-title m-0'>
              <h3 className='fw-bolder m-0'>Formulario de inscripción - Talentos</h3>
            </div>
          </div>
        </div>

        {/* SECTION: Información Personal */}
        <div className='card mb-5'>
          <div className='card-header'>
            <h3 className='card-title'>Información Personal</h3>
          </div>
          <div className='card-body'>
            <div className='row'>
              {/* Nombre */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Nombre <span className='text-danger'>*</span>
                </label>
                <RCInput
                  hideLabel
                  fieldsProps={formik.getFieldProps('name')}
                  i18n={'PLACEHOLDERS.NAME'}
                  value={formik.values.name || ''}
                />
                {formik.touched.name && formik.errors.name && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{String(formik.errors.name)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Apellidos */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Apellidos <span className='text-danger'>*</span>
                </label>
                <RCInput
                  hideLabel
                  fieldsProps={formik.getFieldProps('lastName')}
                  i18n={'PLACEHOLDERS.LAST_NAME'}
                  value={formik.values.lastName || ''}
                />
                {formik.touched.lastName && formik.errors.lastName && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{String(formik.errors.lastName)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Email */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Email <span className='text-danger'>*</span>
                </label>
                <RCInput
                  hideLabel
                  fieldsProps={formik.getFieldProps('email')}
                  i18n={'PLACEHOLDERS.EMAIL'}
                  value={formik.values.email || ''}
                />
                {formik.touched.email && formik.errors.email && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{String(formik.errors.email)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Teléfono */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Teléfono <span className='text-danger'>*</span>
                </label>
                <RCInput
                  hideLabel
                  fieldsProps={formik.getFieldProps('phone')}
                  i18n={'PLACEHOLDERS.PHONE'}
                  value={formik.values.phone || ''}
                />
                {formik.touched.phone && formik.errors.phone && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{String(formik.errors.phone)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Teléfono secundario */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>Teléfono secundario</label>
                <RCInput
                  hideLabel
                  fieldsProps={formik.getFieldProps('secondaryPhone')}
                  i18n={'PLACEHOLDERS.SECONDARY_PHONE'}
                  value={formik.values.secondaryPhone || ''}
                />
              </div>

              {/* Instagram */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>Instagram</label>
                <RCInput
                  hideLabel
                  fieldsProps={formik.getFieldProps('instagram')}
                  i18n={'PLACEHOLDERS.INSTAGRAM'}
                  value={formik.values.instagram || ''}
                />
              </div>

              {/* Dirección */}
              <div className='col-lg-12 col-md-12 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Dirección (residencia habitual) <span className='text-danger'>*</span>
                </label>
                <RCLocationSelect
                  hideLabel
                  i18n={'PLACEHOLDERS.ADDRESS'}
                  value={formik.values.address || ''}
                  touched={formik.touched.address}
                  error={formik.errors.address}
                  onChange={(addr: IAddress) => {
                    setSelectedAddress(addr)
                    formik.setFieldValue('address', addr.fullAddress)
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* SECTION: Datos Personales */}
        <div className='card mb-5'>
          <div className='card-header'>
            <h3 className='card-title'>Datos Personales</h3>
          </div>
          <div className='card-body'>
            <div className='row'>
              {/* Nacionalidad */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Nacionalidad <span className='text-danger'>*</span>
                </label>
                <RCMultiSelect
                  options={(futils.talents?.personalInformation?.nationalities || []).map((o: string) => ({key: o, value: intl.formatMessage({id: o})}))}
                  hideLabel
                  fieldsProps={formik.getFieldProps('nationality')}
                  i18n={'PLACEHOLDERS.NATIONALITY'}
                  value={formik.values.nationality}
                  onChange={(v: string[]) => formik.setFieldValue('nationality', v)}
                />
                {formik.touched.nationality && formik.errors.nationality && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{String(formik.errors.nationality)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Origen */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Origen <span className='text-danger'>*</span>
                </label>
                <RCMultiSelect
                  options={(futils.talents?.personalInformation?.nationalities || []).map((o: string) => ({key: o, value: intl.formatMessage({id: o})}))}
                  hideLabel
                  fieldsProps={formik.getFieldProps('origin')}
                  i18n={'PLACEHOLDERS.ORIGIN'}
                  value={formik.values.origin}
                  onChange={(v: string[]) => formik.setFieldValue('origin', v)}
                />
                {formik.touched.origin && formik.errors.origin && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{String(formik.errors.origin)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Etnia */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Etnia <span className='text-danger'>*</span>
                </label>
                <RCMultiSelect
                  options={(futils.talents?.personalInformation?.ethnicity || []).map((o: string) => ({key: o, value: intl.formatMessage({id: o})}))}
                  hideLabel
                  fieldsProps={formik.getFieldProps('ethnicity')}
                  i18n={'PLACEHOLDERS.ETHNICITIES'}
                  value={formik.values.ethnicity}
                  onChange={(v: string[]) => formik.setFieldValue('ethnicity', v)}
                />
                {formik.touched.ethnicity && formik.errors.ethnicity && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{String(formik.errors.ethnicity)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Fecha nacimiento */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Fecha nacimiento <span className='text-danger'>*</span>
                </label>
                <RCDatePickerInput
                  hideLabel
                  allDay
                  fieldsProps={formik.getFieldProps('birthDate')}
                  i18n={'PLACEHOLDERS.BIRTH_DATE'}
                  value={formik.values.birthDate}
                />
                {formik.touched.birthDate && formik.errors.birthDate && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{String(formik.errors.birthDate)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Género */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Género <span className='text-danger'>*</span>
                </label>
                <RCSelect
                  options={(futils.talents?.personalInformation?.gender || []).map((o: string) => ({key: o, value: intl.formatMessage({id: o})}))}
                  hideLabel
                  fieldsProps={formik.getFieldProps('gender')}
                  i18n={'PLACEHOLDERS.GENDER'}
                  value={formik.values.gender}
                />
                {formik.touched.gender && formik.errors.gender && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{String(formik.errors.gender)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Profesiones */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>Profesiones</label>
                <RCMultiSelect
                  options={(futils.talents?.personalInformation?.professions || []).map((o: string) => ({key: o, value: intl.formatMessage({id: o})}))}
                  hideLabel
                  fieldsProps={formik.getFieldProps('professions')}
                  i18n={'PLACEHOLDERS.PROFESSIONS'}
                  value={formik.values.professions}
                  onChange={(v: string[]) => formik.setFieldValue('professions', v)}
                />
              </div>

              {/* LGTBIQ+ */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <RCSwitch
                  label='LGTBIQ+'
                  fieldsProps={{
                    ...formik.getFieldProps('lgtbiq'),
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                      formik.setFieldValue('lgtbiq', e.target.checked)
                    },
                  }}
                  i18n={'PLACEHOLDERS.LGTBIQ'}
                  value={formik.values.lgtbiq}
                />
              </div>
            </div>
          </div>
        </div>

        {/* SECTION: Experiencia */}
        <div className='card mb-5'>
          <div className='card-header'>
            <h3 className='card-title'>Experiencia</h3>
          </div>
          <div className='card-body'>
            <div className='row'>
              {/* Tipo de actor */}
              <div className='col-lg-6 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Tipo de actor <span className='text-danger'>*</span>
                </label>
                <RCMultiSelect
                  options={(futils.talents?.experience?.categories || []).map((o: string) => ({key: o, value: intl.formatMessage({id: o})}))}
                  hideLabel
                  fieldsProps={formik.getFieldProps('actorCategory')}
                  i18n={'PLACEHOLDERS.ACTOR_TYPE'}
                  value={formik.values.actorCategory}
                  onChange={(v: string[]) => formik.setFieldValue('actorCategory', v)}
                />
                {formik.touched.actorCategory && formik.errors.actorCategory && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{String(formik.errors.actorCategory)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Biografía */}
              <div className='col-lg-6 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>Biografía</label>
                <textarea
                  className='form-control form-control-solid'
                  rows={4}
                  placeholder='Biografía'
                  {...formik.getFieldProps('biography')}
                  value={formik.values.biography || ''}
                />
              </div>
            </div>
          </div>
        </div>

        {/* SECTION: Características Físicas */}
        <div className='card mb-5'>
          <div className='card-header'>
            <h3 className='card-title'>Características Físicas</h3>
          </div>
          <div className='card-body'>
            <div className='row'>
              {/* Altura */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Altura (cm) <span className='text-danger'>*</span>
                </label>
                <RCSelect
                  options={getValuesAsArray(50, 220).map((v) => ({key: v.toString(), value: v.toString()}))}
                  hideLabel
                  fieldsProps={formik.getFieldProps('height')}
                  i18n={'PLACEHOLDERS.HEIGHT'}
                  value={formik.values.height}
                />
                {formik.touched.height && formik.errors.height && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{String(formik.errors.height)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Pecho */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>Pecho (cm)</label>
                <RCSelect
                  options={getValuesAsArray(20, 200).map((v) => ({key: v.toString(), value: v.toString()}))}
                  hideLabel
                  fieldsProps={formik.getFieldProps('bust')}
                  i18n={'PLACEHOLDERS.BUST'}
                  value={formik.values.bust}
                />
              </div>

              {/* Cintura */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>Cintura (cm)</label>
                <RCSelect
                  options={getValuesAsArray(20, 200).map((v) => ({key: v.toString(), value: v.toString()}))}
                  hideLabel
                  fieldsProps={formik.getFieldProps('waist')}
                  i18n={'PLACEHOLDERS.WAIST'}
                  value={formik.values.waist}
                />
              </div>

              {/* Cadera */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>Cadera (cm)</label>
                <RCSelect
                  options={getValuesAsArray(20, 200).map((v) => ({key: v.toString(), value: v.toString()}))}
                  hideLabel
                  fieldsProps={formik.getFieldProps('hip')}
                  i18n={'PLACEHOLDERS.HIP'}
                  value={formik.values.hip}
                />
              </div>

              {/* Talla camisa */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Talla camisa <span className='text-danger'>*</span>
                </label>
                <RCSelect
                  options={clothingSizeOptions}
                  hideLabel
                  fieldsProps={formik.getFieldProps('dressSize')}
                  i18n={'PLACEHOLDERS.DRESS_SIZE'}
                  value={formik.values.dressSize}
                />
                {formik.touched.dressSize && formik.errors.dressSize && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{String(formik.errors.dressSize)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Talla pantalón */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Talla pantalón <span className='text-danger'>*</span>
                </label>
                <RCSelect
                  options={clothingSizeOptions}
                  hideLabel
                  fieldsProps={formik.getFieldProps('pantSize')}
                  i18n={'PLACEHOLDERS.PANT_SIZE'}
                  value={formik.values.pantSize}
                />
                {formik.touched.pantSize && formik.errors.pantSize && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{String(formik.errors.pantSize)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Talla zapatos */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Talla zapatos <span className='text-danger'>*</span>
                </label>
                <RCSelect
                  options={getValuesAsArray(10, 50).map((v) => ({key: v.toString(), value: v.toString()}))}
                  hideLabel
                  fieldsProps={formik.getFieldProps('shoesSize')}
                  i18n={'PLACEHOLDERS.SHOES_SIZE'}
                  value={formik.values.shoesSize}
                />
                {formik.touched.shoesSize && formik.errors.shoesSize && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{String(formik.errors.shoesSize)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Pelo / Color */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Pelo / Color <span className='text-danger'>*</span>
                </label>
                <RCSelect
                  options={(futils.talents?.physicalCharacteristics?.hairColor || []).map((o: string) => ({key: o, value: intl.formatMessage({id: o})}))}
                  hideLabel
                  fieldsProps={formik.getFieldProps('hairColor')}
                  i18n={'PLACEHOLDERS.HAIR_COLOR'}
                  value={formik.values.hairColor}
                />
                {formik.touched.hairColor && formik.errors.hairColor && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{String(formik.errors.hairColor)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Tipo de pelo */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Tipo de pelo <span className='text-danger'>*</span>
                </label>
                <RCSelect
                  options={(futils.talents?.physicalCharacteristics?.hairType || []).map((o: string) => ({key: o, value: intl.formatMessage({id: o})}))}
                  hideLabel
                  fieldsProps={formik.getFieldProps('hairType')}
                  i18n={'PLACEHOLDERS.HAIR_TYPE'}
                  value={formik.values.hairType}
                />
                {formik.touched.hairType && formik.errors.hairType && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{String(formik.errors.hairType)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Longitud pelo */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>Longitud pelo</label>
                <RCSelect
                  options={(futils.talents?.physicalCharacteristics?.hairLength || []).map((o: string) => ({key: o, value: intl.formatMessage({id: o})}))}
                  hideLabel
                  fieldsProps={formik.getFieldProps('hairLength')}
                  i18n={'PLACEHOLDERS.HAIR_LENGTH'}
                  value={formik.values.hairLength}
                />
              </div>

              {/* Ojos */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Ojos <span className='text-danger'>*</span>
                </label>
                <RCSelect
                  options={(futils.talents?.physicalCharacteristics?.eyesColor || []).map((o: string) => ({key: o, value: intl.formatMessage({id: o})}))}
                  hideLabel
                  fieldsProps={formik.getFieldProps('eyesColor')}
                  i18n={'PLACEHOLDERS.EYES_COLOR'}
                  value={formik.values.eyesColor}
                />
                {formik.touched.eyesColor && formik.errors.eyesColor && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{String(formik.errors.eyesColor)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Tipo cuerpo */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Tipo cuerpo <span className='text-danger'>*</span>
                </label>
                <RCSelect
                  options={(futils.talents?.physicalCharacteristics?.bodyType || []).map((o: string) => ({key: o, value: intl.formatMessage({id: o})}))}
                  hideLabel
                  fieldsProps={formik.getFieldProps('bodyType')}
                  i18n={'PLACEHOLDERS.BODY_TYPE'}
                  value={formik.values.bodyType}
                />
                {formik.touched.bodyType && formik.errors.bodyType && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{String(formik.errors.bodyType)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Tipo piel */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>Tipo piel</label>
                <RCSelect
                  options={(futils.talents?.physicalCharacteristics?.skinType || []).map((o: string) => ({key: o, value: intl.formatMessage({id: o})}))}
                  hideLabel
                  fieldsProps={formik.getFieldProps('skinType')}
                  i18n={'PLACEHOLDERS.SKIN_TYPE'}
                  value={formik.values.skinType}
                />
              </div>

              {/* Gafas */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <RCSwitch
                  label='Gafas'
                  fieldsProps={{
                    ...formik.getFieldProps('glasses'),
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                      formik.setFieldValue('glasses', e.target.checked)
                    },
                  }}
                  i18n={'PLACEHOLDERS.GLASSES'}
                  value={formik.values.glasses}
                />
              </div>

              {/* Lentillas */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <RCSwitch
                  label='Lentillas'
                  fieldsProps={{
                    ...formik.getFieldProps('contactLenses'),
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                      formik.setFieldValue('contactLenses', e.target.checked)
                    },
                  }}
                  i18n={'PLACEHOLDERS.CONTACT_LENSES'}
                  value={formik.values.contactLenses}
                />
              </div>
            </div>
          </div>
        </div>

        {/* SECTION: Idiomas */}
        <div className='card mb-5'>
          <div className='card-header'>
            <h3 className='card-title'>Idiomas</h3>
          </div>
          <div className='card-body'>
            <div className='row'>
              {/* Idiomas */}
              <div className='col-lg-6 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Idiomas <span className='text-danger'>*</span>
                </label>
                <RCMultiSelect
                  options={(futils.talents?.language?.languages || []).map((o: string) => ({key: o, value: intl.formatMessage({id: o})}))}
                  hideLabel
                  fieldsProps={formik.getFieldProps('languages')}
                  i18n={'PLACEHOLDERS.LANGUAGES'}
                  value={formik.values.languages}
                  onChange={(v: string[]) => formik.setFieldValue('languages', v)}
                />
                {formik.touched.languages && formik.errors.languages && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{String(formik.errors.languages)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Dialectos */}
              <div className='col-lg-6 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Dialectos <span className='text-danger'>*</span>
                </label>
                <RCMultiSelect
                  options={(futils.talents?.language?.dialects || []).map((o: string) => ({key: o, value: intl.formatMessage({id: o})}))}
                  hideLabel
                  fieldsProps={formik.getFieldProps('dialects')}
                  i18n={'PLACEHOLDERS.DIALECTS'}
                  value={formik.values.dialects}
                  onChange={(v: string[]) => formik.setFieldValue('dialects', v)}
                />
                {formik.touched.dialects && formik.errors.dialects && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{String(formik.errors.dialects)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* SECTION: Habilidades */}
        <div className='card mb-5'>
          <div className='card-header'>
            <h3 className='card-title'>Habilidades</h3>
          </div>
          <div className='card-body'>
            <div className='row'>
              {/* Carné de conducir */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Carné de conducir <span className='text-danger'>*</span>
                </label>
                <RCMultiSelect
                  options={(futils.talents?.skills?.driverLicence || []).map((o: string) => ({key: o, value: intl.formatMessage({id: o})}))}
                  hideLabel
                  fieldsProps={formik.getFieldProps('driverLicence')}
                  i18n={'PLACEHOLDERS.DRIVER_LICENSE'}
                  value={formik.values.driverLicence}
                  onChange={(v: string[]) => formik.setFieldValue('driverLicence', v)}
                />
                {formik.touched.driverLicence && formik.errors.driverLicence && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{String(formik.errors.driverLicence)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Deportes */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Deportes <span className='text-danger'>*</span>
                </label>
                <RCMultiSelect
                  options={(futils.talents?.skills?.sports || []).map((o: string) => ({key: o, value: intl.formatMessage({id: o})}))}
                  hideLabel
                  fieldsProps={formik.getFieldProps('sports')}
                  i18n={'PLACEHOLDERS.SPORTS'}
                  value={formik.values.sports}
                  onChange={(v: string[]) => formik.setFieldValue('sports', v)}
                />
                {formik.touched.sports && formik.errors.sports && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{String(formik.errors.sports)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Bailes */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Bailes <span className='text-danger'>*</span>
                </label>
                <RCMultiSelect
                  options={(futils.talents?.skills?.dancing || []).map((o: string) => ({key: o, value: intl.formatMessage({id: o})}))}
                  hideLabel
                  fieldsProps={formik.getFieldProps('dancing')}
                  i18n={'PLACEHOLDERS.DANCING'}
                  value={formik.values.dancing}
                  onChange={(v: string[]) => formik.setFieldValue('dancing', v)}
                />
                {formik.touched.dancing && formik.errors.dancing && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{String(formik.errors.dancing)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Instrumentos */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Instrumentos <span className='text-danger'>*</span>
                </label>
                <RCMultiSelect
                  options={(futils.talents?.skills?.instruments || []).map((o: string) => ({key: o, value: intl.formatMessage({id: o})}))}
                  hideLabel
                  fieldsProps={formik.getFieldProps('instruments')}
                  i18n={'PLACEHOLDERS.INSTRUMENTS'}
                  value={formik.values.instruments}
                  onChange={(v: string[]) => formik.setFieldValue('instruments', v)}
                />
                {formik.touched.instruments && formik.errors.instruments && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{String(formik.errors.instruments)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* ¿Cantas? */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <RCSwitch
                  label='¿Cantas?'
                  fieldsProps={{
                    ...formik.getFieldProps('singing'),
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                      formik.setFieldValue('singing', e.target.checked)
                    },
                  }}
                  i18n={'PLACEHOLDERS.SINGING'}
                  value={formik.values.singing}
                />
              </div>
            </div>
          </div>
        </div>

        {/* SECTION: Archivos */}
        <div className='card mb-5'>
          <div className='card-header'>
            <h3 className='card-title'>Archivos</h3>
          </div>
          <div className='card-body'>
            <div className='row'>
              <div className='col-lg-4 col-md-6 mb-6'>
                <PhotoUploadField
                  label='Foto primer plano'
                  file={closeUpPhoto}
                  onFileSelect={(file) => setCloseUpPhoto(file)}
                  required
                />
                <p className='text-muted fs-7 mt-2'>
                  Foto actual de primer plano (cara). Sin filtros ni gafas de sol. Fondo neutro. Buena iluminación.
                </p>
              </div>

              <div className='col-lg-4 col-md-6 mb-6'>
                <PhotoUploadField
                  label='Foto cuerpo entero'
                  file={fullBodyPhoto}
                  onFileSelect={(file) => setFullBodyPhoto(file)}
                  required
                />
                <p className='text-muted fs-7 mt-2'>
                  Foto actual de cuerpo entero. Sin filtros. Ropa ajustada que permita ver la silueta. Fondo neutro. Buena iluminación.
                </p>
              </div>

              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Vídeo presentación <span className='text-danger'>*</span>
                </label>
                <p className='text-muted fs-7 mb-3'>
                  Vídeo actual de presentación. En primer plano. Sin filtros. Queremos conocerte; dinos tu nombre (no digas tu edad), provincia de residencia actual, si tienes experiencia o formación en el sector de la moda, la publicidad o la ficción. Háblanos de tus aficiones o quizás de tus habilidades. Y acaba con una sonrisa a cámara. Entre 40 y 60 segundos.
                </p>
                <PhotoUploadField
                  label='Vídeo presentación'
                  file={presentationVideo}
                  onFileSelect={(file) => setPresentationVideo(file)}
                  accept={{'video/*': ['.mp4', '.mov', '.avi']}}
                  required
                />
              </div>
            </div>

            {fileErrors.length > 0 && (
              <div className='alert alert-danger mt-3'>
                {fileErrors.map((err, idx) => (
                  <div key={idx}>{err}</div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Submit error */}
        {submitError && (
          <div className='alert alert-danger mb-5'>{submitError}</div>
        )}

        {/* Submit button */}
        <div className='d-flex justify-content-end mb-10'>
          <button type='submit' className='btn btn-primary'>
            Enviar formulario
          </button>
        </div>
      </form>
    </div>
  )
}

export {TalentosSignupForm}
